<template>
  <DashboardLayout>

    <div class="d-flex align-items-center gap-2 mb-4 page-identifier">
      <router-link :to="{ name: 'ViewBank', params: { id: id } }"
        class="mb-0 main-identifier fw-semibold text-decoration-none" title="Go back to Bank">
        Bank
      </router-link>
      <span class="material-symbols-outlined">keyboard_arrow_right</span>
      <p class="mb-0 sub-identifier">Edit Bank</p>
    </div>

    <div class="d-flex justify-content-center">
      <div class="form-outer">
        <form @submit.prevent="handleSubmit">
          <div class="crud-form position-relative">

            <div class="form-inner">
              <div class="form-header d-flex justify-content-between align-items-center">
                <h1 class="mb-0">Edit Bank</h1>
              </div>

              <div class="form-body">
                <div v-if="fetchLoading" class="text-center py-5">
                  <div class="spinner-border text-primary" role="status" aria-hidden="true"></div>
                  <p class="mt-2 mb-0 text-muted">Loading bank details...</p>
                </div>

                <div v-else>
                  <div class="row mt-2 mb-3">

                    <div class="col-md-12 row form-group position-relative floating-label-group">
                      <div class="col-md-3 label-left d-flex justify-content-end align-items-center">
                        <label for="bankName" class="form-label">Bank Name:</label>
                      </div>
                      <div class="col-md-9 input-right">
                        <input type="text" class="form-control" id="bankName" v-model="form.bankName"
                          placeholder="Enter bank name" required>
                      </div>
                    </div>

                    <div class="col-md-12 row form-group position-relative floating-label-group">
                      <div class="col-md-3 label-left d-flex justify-content-end align-items-center">
                        <label for="bankCodeName" class="form-label">Bank Code:</label>
                      </div>
                      <div class="col-md-9 input-right">
                        <input type="text" class="form-control" id="bankCodeName" v-model="form.bankCodeName"
                          placeholder="Enter bank code" required>
                      </div>
                    </div>

                    <div class="col-md-12 row form-group position-relative floating-label-group">
                      <div class="col-md-3 label-left d-flex justify-content-end align-items-center">
                        <label for="bankSortCode" class="form-label">Sort Code:</label>
                      </div>
                      <div class="col-md-9 input-right">
                        <input type="text" class="form-control" id="bankSortCode" v-model="form.bankSortCode"
                          placeholder="Enter sort code" required>
                      </div>
                    </div>

                  </div>

                  <div class="d-flex justify-content-center">
                    <button class="submit-btn" type="submit" :disabled="isLoading">
                      <span v-if="isLoading" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      {{ isLoading ? 'Submitting...' : 'Submit' }}
                    </button>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </form>
      </div>
    </div>

  </DashboardLayout>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import axios from 'axios'
import Cookies from 'js-cookie'
import DashboardLayout from '../layout/DashboardLayout.vue'
import Swal from 'sweetalert2'

const root_api_url = process.env.VUE_APP_API_ROOT_URL
const route = useRoute()
const router = useRouter()
const id = route.params.id

const isLoading = ref(false)
const fetchLoading = ref(true)

const form = ref({
  bankName: '',
  bankCodeName: '',
  bankSortCode: '',
})

const iconHtmlSuccess = `<span class="iconify" data-icon="ci:circle-check" style="font-size: 100px; color: green;"></span>`
const iconHtmlError = `<span class="iconify" data-icon="material-symbols:cancel-outline-rounded" style="font-size: 100px; color: red;"></span>`

const fetchBank = async () => {
  const token = Cookies.get('auth_token')
  fetchLoading.value = true
  try {
    const response = await axios.get(`${root_api_url}/payment/banks/${id}`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    })
    const data = response.data
    form.value.bankName = data.bankName ?? ''
    form.value.bankCodeName = data.bankCodeName ?? ''
    form.value.bankSortCode = data.bankSortCode ?? ''
  } catch (error) {
    console.error('Failed to fetch bank:', error)
    Swal.fire({
      html: `${iconHtmlError}<h1 style="font-weight:600;">Error</h1><p>Failed to load bank details.</p>`,
      showConfirmButton: true,
      showCloseButton: true,
      icon: undefined,
    })
  } finally {
    fetchLoading.value = false
  }
}

const handleSubmit = async () => {
  const token = Cookies.get('auth_token')
  isLoading.value = true
  try {
    const result = await axios.put(`${root_api_url}/payment/${id}`, form.value, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    })

    if (result.status === 200) {
      await Swal.fire({
        html: `${iconHtmlSuccess}<h1 style="font-weight:600;">Success</h1><p>Bank updated successfully.</p>`,
        showConfirmButton: true,
        showCloseButton: true,
        icon: undefined,
      })
      router.push({ name: 'ViewBank', params: { id } })
    }
  } catch (error) {
    if (error.response) {
      Swal.fire({
        html: `${iconHtmlError}<h1 style="font-weight:600;">Error</h1><p>${error?.response?.data?.errorMessage ?? error?.response?.data?.error ?? 'Failed to update bank.'}</p>`,
        showConfirmButton: true,
        showCloseButton: true,
        icon: undefined,
      })
    } else {
      Swal.fire({
        html: `${iconHtmlError}<h1 style="font-weight:600;">Error</h1><p>Unexpected error occurred while updating bank. Kindly contact administrator</p>`,
        showConfirmButton: true,
        showCloseButton: true,
        icon: undefined,
      })
    }
  } finally {
    isLoading.value = false
  }
}

onMounted(async () => {
  await fetchBank()
  document.title = 'Edit Bank'
})
</script>

<style scoped>
.submit-btn {
  background-color: #3498db !important;
  border-color: #3498db !important;
}

.submit-btn:hover {
  background-color: #2980b9 !important;
  border-color: #2980b9 !important;
}

/* Form Container */
.form-outer {
  width: 100%;
  max-width: 800px;
  /* padding: 1rem; */
}
</style>
